<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col cols="12" xl="12">
        <transition name="slide">
          <b-card>
            <template #header>
              <div class="d-flex justify-content-between align-items-center">
                <h5 class="m-0">Gerenciar Turmas</h5>
                <div>
                  <b-button
                    variant="info"
                    size="sm"
                    class="mr-2"
                    @click="$router.push('/ead/dashboard')"
                  >
                    <i class="fa fa-arrow-left mr-1"></i>
                    Voltar
                  </b-button>
                  <b-button
                    variant="primary"
                    size="sm"
                    @click="openAddClassPanel"
                  >
                    <i class="fa fa-plus mr-1"></i>
                    Nova Turma
                  </b-button>
                </div>
              </div>
            </template>

            <b-card-body>
              <!-- Filtros -->
              <div class="filters-section mb-3">
                <b-form-group label="Curso" class="filter-item">
                  <b-form-select
                    v-model="filterCourseId"
                    :options="courseFilterOptions"
                    @change="loadClasses"
                  >
                    <template #first>
                      <b-form-select-option :value="null">
                        Todos os cursos
                      </b-form-select-option>
                    </template>
                  </b-form-select>
                </b-form-group>

                <b-form-group label="Status" class="filter-item">
                  <b-form-select
                    v-model="filterStatus"
                    :options="statusOptions"
                    @change="loadClasses"
                  >
                    <template #first>
                      <b-form-select-option :value="null">
                        Todos os status
                      </b-form-select-option>
                    </template>
                  </b-form-select>
                </b-form-group>
              </div>

              <!-- Tabela de Turmas -->
              <div class="table-container">
                <b-table
                  :items="classes"
                  :fields="tableFields"
                  :busy="loading"
                  thead-class="classes-header"
                  borderless
                  hover
                  responsive
                  show-empty
                >
                  <template #table-busy>
                    <div class="text-center py-5">
                      <b-spinner></b-spinner>
                      <span class="ml-2">Carregando...</span>
                    </div>
                  </template>

                  <template #empty>
                    <div class="text-muted text-center py-5">
                      <i class="fas fa-users fa-2x"></i>
                      <p class="mt-3">Nenhuma turma encontrada</p>
                    </div>
                  </template>

                  <template #cell(name)="data">
                    <strong class="class-name">{{ data.item.name }}</strong>
                    <br />
                    <small class="text-muted" v-if="data.item.description">
                      {{ data.item.description }}
                    </small>
                  </template>

                  <template #cell(courseId)="data">
                    <div v-if="data.item.courseId">
                      {{ data.item.courseId.nome }}
                      <br />
                      <small class="text-muted">{{
                        data.item.courseId.categoria
                      }}</small>
                    </div>
                  </template>

                  <template #cell(students)="data">
                    <b-badge variant="info">
                      {{ data.item.students ? data.item.students.length : 0 }}
                      alunos
                    </b-badge>
                  </template>

                  <template #cell(dates)="data">
                    <div v-if="data.item.startDate">
                      <small>
                        <strong>Início:</strong>
                        {{ formatDate(data.item.startDate) }}
                      </small>
                    </div>
                    <div v-if="data.item.endDate">
                      <small>
                        <strong>Fim:</strong>
                        {{ formatDate(data.item.endDate) }}
                      </small>
                    </div>
                    <div v-if="!data.item.startDate && !data.item.endDate">
                      <small class="text-muted">Sem datas definidas</small>
                    </div>
                  </template>

                  <template #cell(status)="data">
                    <b-badge :variant="getStatusVariant(data.item.status)">
                      {{ getStatusLabel(data.item.status) }}
                    </b-badge>
                  </template>

                  <template #cell(actions)="data">
                    <b-button
                      size="sm"
                      variant="link"
                      class="action-btn"
                      @click="openEditClassPanel(data.item)"
                      v-b-tooltip.hover
                      title="Editar"
                    >
                      <i class="fa fa-edit action-icon"></i>
                    </b-button>
                    <b-button
                      size="sm"
                      variant="link"
                      class="action-btn"
                      @click="openStudentsModal(data.item)"
                      v-b-tooltip.hover
                      title="Ver Alunos"
                    >
                      <i class="fa fa-users action-icon"></i>
                    </b-button>
                    <b-button
                      size="sm"
                      variant="link"
                      class="action-btn"
                      @click="confirmDelete(data.item)"
                      v-b-tooltip.hover
                      title="Excluir"
                    >
                      <i class="fa fa-trash action-icon"></i>
                    </b-button>
                  </template>
                </b-table>
              </div>

              <!-- Paginação -->
              <nav class="pagination-nav" v-if="totalClasses > perPage">
                <b-pagination
                  v-model="currentPage"
                  :total-rows="totalClasses"
                  :per-page="perPage"
                  prev-text="Anterior"
                  next-text="Próximo"
                  align="center"
                  @change="onPageChange"
                ></b-pagination>
              </nav>
            </b-card-body>
          </b-card>
        </transition>
      </b-col>
    </b-row>

    <!-- Side Panel: Adicionar/Editar Turma -->
    <b-sidebar
      v-model="showSidePanel"
      :title="editingClass ? 'Editar Turma' : 'Nova Turma'"
      right
      shadow
      width="650px"
      @hidden="resetForm"
      sidebar-class="sidebar-custom"
    >
      <template #default="{ hide }">
        <div class="sidebar-content">
          <b-form @submit.prevent="submitClass">
            <!-- Seção: Informações Básicas -->
            <div class="form-section">
              <div class="section-header">
                <i class="fa fa-info-circle"></i>
                <span>Informações Básicas</span>
              </div>

              <b-form-group
                label="Nome da Turma"
                label-for="class-name"
                class="required-field"
              >
                <b-form-input
                  id="class-name"
                  v-model="form.name"
                  placeholder="Ex: Turma Ética Q1 2025"
                  required
                  size="lg"
                ></b-form-input>
              </b-form-group>

              <b-form-group label="Descrição" label-for="class-description">
                <b-form-textarea
                  id="class-description"
                  v-model="form.description"
                  placeholder="Descreva o objetivo desta turma..."
                  rows="3"
                ></b-form-textarea>
              </b-form-group>
            </div>

            <!-- Seção: Curso -->
            <div class="form-section">
              <div class="section-header">
                <i class="fa fa-graduation-cap"></i>
                <span>Curso</span>
              </div>

              <b-form-group
                label="Selecione o Curso"
                label-for="class-course"
                class="required-field"
              >
                <b-form-select
                  id="class-course"
                  v-model="form.courseId"
                  :options="courseOptions"
                  required
                  :disabled="loadingCourses"
                  size="lg"
                >
                  <template #first>
                    <b-form-select-option :value="null" disabled>
                      {{
                        loadingCourses
                          ? "Carregando cursos..."
                          : "-- Selecione um curso --"
                      }}
                    </b-form-select-option>
                  </template>
                </b-form-select>
              </b-form-group>
            </div>

            <!-- Seção: Período -->
            <div class="form-section">
              <div class="section-header">
                <i class="fa fa-calendar"></i>
                <span>Período (Opcional)</span>
              </div>

              <b-row>
                <b-col md="6">
                  <b-form-group label="Início" label-for="start-date">
                    <b-form-input
                      id="start-date"
                      v-model="form.startDate"
                      type="date"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Término" label-for="end-date">
                    <b-form-input
                      id="end-date"
                      v-model="form.endDate"
                      type="date"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>
            </div>

            <!-- Seção: Colaboradores -->
            <div class="form-section">
              <div class="section-header">
                <i class="fa fa-users"></i>
                <span>Seleção de Colaboradores</span>
              </div>

              <b-form-group>
                <b-form-radio-group
                  v-model="form.selectionType"
                  :options="selectionTypeOptions"
                  buttons
                  button-variant="outline-primary"
                  size="sm"
                  class="selection-type-buttons"
                  @change="resetEmployeeSelection"
                ></b-form-radio-group>
              </b-form-group>

              <!-- Seleção Individual -->
              <div
                v-if="form.selectionType === 'individual'"
                class="selection-content"
              >
                <b-button
                  variant="outline-primary"
                  size="sm"
                  block
                  @click="openEmployeeModal"
                  class="mb-3"
                >
                  <i class="fa fa-search mr-2"></i>
                  Buscar Colaboradores
                </b-button>

                <div
                  v-if="selectedEmployees.length > 0"
                  class="selected-employees"
                >
                  <small class="text-muted d-block mb-2">
                    {{ selectedEmployees.length }} colaborador(es)
                    selecionado(s)
                  </small>
                  <div class="employee-list">
                    <div
                      v-for="emp in selectedEmployees"
                      :key="emp._id"
                      class="employee-item"
                    >
                      <div class="employee-info">
                        <div class="employee-name">
                          {{ emp.name }} {{ emp.surname }}
                        </div>
                        <div class="employee-email">{{ emp.email }}</div>
                      </div>
                      <b-button
                        variant="link"
                        size="sm"
                        class="remove-btn"
                        @click="removeEmployee(emp._id)"
                      >
                        <i class="fa fa-times"></i>
                      </b-button>
                    </div>
                  </div>
                </div>
                <b-alert v-else show variant="light" class="text-center mb-0">
                  <i class="fa fa-info-circle"></i>
                  Nenhum colaborador selecionado
                </b-alert>
              </div>

              <!-- Seleção por Área/Setor -->
              <div
                v-if="form.selectionType === 'department'"
                class="selection-content"
              >
                <b-form-group
                  label="Área/Setor"
                  label-for="department-select"
                  class="required-field"
                >
                  <b-form-select
                    id="department-select"
                    v-model="form.departmentId"
                    :options="departmentOptions"
                    required
                    :disabled="loadingDepartments"
                  >
                    <template #first>
                      <b-form-select-option :value="null" disabled>
                        {{
                          loadingDepartments
                            ? "Carregando..."
                            : "-- Selecione uma área/setor --"
                        }}
                      </b-form-select-option>
                    </template>
                  </b-form-select>
                </b-form-group>

                <b-alert
                  v-if="previewEmployees.length > 0"
                  show
                  variant="info"
                  class="preview-alert"
                >
                  <div
                    class="d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <i class="fa fa-check-circle mr-2"></i>
                      <strong>{{ previewEmployees.length }}</strong>
                      colaborador(es) serão matriculados
                    </div>
                    <b-button
                      variant="link"
                      size="sm"
                      @click="showPreviewModal = true"
                    >
                      Ver lista
                    </b-button>
                  </div>
                </b-alert>
              </div>

              <!-- Seleção de Terceiros -->
              <div
                v-if="form.selectionType === 'thirdParty'"
                class="selection-content"
              >
                <b-button
                  variant="outline-primary"
                  size="sm"
                  block
                  @click="openThirdPartyModal"
                  class="mb-3"
                >
                  <i class="fa fa-search mr-2"></i>
                  Buscar Terceiros
                </b-button>

                <div
                  v-if="selectedThirdParties.length > 0"
                  class="selected-employees"
                >
                  <small class="text-muted d-block mb-2">
                    {{ selectedThirdParties.length }} terceiro(s) selecionado(s)
                  </small>
                  <div class="employee-list">
                    <div
                      v-for="tp in selectedThirdParties"
                      :key="tp._id"
                      class="employee-item"
                    >
                      <div class="employee-info">
                        <div class="employee-name">
                          {{ tp.name }}
                        </div>
                        <div class="employee-email">{{ tp.email }}</div>
                      </div>
                      <b-button
                        variant="link"
                        size="sm"
                        class="remove-btn"
                        @click="removeThirdParty(tp._id)"
                      >
                        <i class="fa fa-times"></i>
                      </b-button>
                    </div>
                  </div>
                </div>
                <b-alert v-else show variant="light" class="text-center mb-0">
                  <i class="fa fa-info-circle"></i>
                  Nenhum terceiro selecionado
                </b-alert>
              </div>

              <!-- Seleção de Todos -->
              <div
                v-if="form.selectionType === 'all'"
                class="selection-content"
              >
                <b-alert
                  v-if="loadingAllParticipants"
                  show
                  variant="light"
                  class="text-center"
                >
                  <b-spinner small class="mr-2"></b-spinner>
                  Carregando colaboradores e terceiros...
                </b-alert>

                <b-alert
                  v-else-if="previewAllParticipants.length > 0"
                  show
                  variant="info"
                  class="preview-alert"
                >
                  <div
                    class="d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <i class="fa fa-check-circle mr-2"></i>
                      <strong>{{ previewAllParticipants.length }}</strong>
                      participante(s) serão matriculados
                      <small class="d-block text-muted mt-1">
                        ({{ previewEmployeesCount }} colaboradores +
                        {{ previewThirdPartiesCount }} terceiros)
                      </small>
                    </div>
                    <b-button
                      variant="link"
                      size="sm"
                      @click="showPreviewAllModal = true"
                    >
                      Ver lista
                    </b-button>
                  </div>
                </b-alert>

                <b-alert v-else show variant="light" class="text-center mb-0">
                  <i class="fa fa-info-circle"></i>
                  Nenhum participante encontrado
                </b-alert>
              </div>
            </div>

            <!-- Botões de Ação -->
            <div class="form-actions">
              <b-button
                type="submit"
                variant="primary"
                size="lg"
                :disabled="submitting"
                block
              >
                <b-spinner small v-if="submitting" class="mr-2"></b-spinner>
                <i v-else class="fa fa-check mr-2"></i>
                {{
                  submitting
                    ? "Salvando..."
                    : editingClass
                    ? "Atualizar Turma"
                    : "Criar Turma"
                }}
              </b-button>
              <b-button variant="light" size="lg" block @click="hide">
                <i class="fa fa-times mr-2"></i>
                Cancelar
              </b-button>
            </div>
          </b-form>
        </div>
      </template>
    </b-sidebar>

    <!-- Modal: Selecionar Colaboradores -->
    <b-modal
      v-model="showEmployeeModal"
      title="Selecionar Colaboradores"
      size="lg"
      @ok="confirmEmployeeSelection"
    >
      <b-form-group label="Buscar" label-for="employee-search">
        <b-form-input
          id="employee-search"
          v-model="employeeSearch"
          placeholder="Digite nome ou email..."
        ></b-form-input>
      </b-form-group>

      <b-table
        :items="filteredEmployees"
        :fields="employeeTableFields"
        :busy="loadingEmployees"
        striped
        hover
        selectable
        select-mode="multi"
        @row-selected="onRowSelected"
        ref="employeeTable"
      >
        <template #cell(selected)="{ rowSelected }">
          <template v-if="rowSelected">
            <i class="fa fa-check text-success"></i>
          </template>
          <template v-else>
            <i class="fa fa-square-o text-muted"></i>
          </template>
        </template>

        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong>Carregando...</strong>
          </div>
        </template>
      </b-table>
    </b-modal>

    <!-- Modal: Selecionar Terceiros -->
    <b-modal
      v-model="showThirdPartyModal"
      title="Selecionar Terceiros"
      size="lg"
      @ok="confirmThirdPartySelection"
    >
      <b-form-group label="Buscar" label-for="third-party-search">
        <b-form-input
          id="third-party-search"
          v-model="thirdPartySearch"
          placeholder="Digite nome ou email..."
        ></b-form-input>
      </b-form-group>

      <b-table
        :items="filteredThirdParties"
        :fields="thirdPartyTableFields"
        :busy="loadingThirdParties"
        striped
        hover
        selectable
        select-mode="multi"
        @row-selected="onThirdPartyRowSelected"
        ref="thirdPartyTable"
      >
        <template #cell(selected)="{ rowSelected }">
          <template v-if="rowSelected">
            <i class="fa fa-check text-success"></i>
          </template>
          <template v-else>
            <i class="fa fa-square-o text-muted"></i>
          </template>
        </template>

        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong>Carregando...</strong>
          </div>
        </template>

        <template #empty>
          <div class="text-muted text-center py-3">
            <i class="fa fa-users fa-2x"></i>
            <p class="mt-2">Nenhum terceiro encontrado</p>
          </div>
        </template>
      </b-table>
    </b-modal>

    <!-- Modal: Preview de Colaboradores -->
    <b-modal
      v-model="showPreviewModal"
      title="Colaboradores que serão matriculados"
      size="lg"
      ok-only
    >
      <b-table
        :items="previewEmployees"
        :fields="previewTableFields"
        striped
        hover
        responsive
      ></b-table>
    </b-modal>

    <!-- Modal: Ver Alunos da Turma -->
    <b-modal
      v-model="showStudentsModal"
      :title="
        selectedClass ? `Alunos - ${selectedClass.name}` : 'Alunos da Turma'
      "
      size="lg"
      ok-only
    >
      <b-table
        v-if="selectedClass"
        :items="selectedClass.students"
        :fields="studentsTableFields"
        striped
        hover
        responsive
      >
        <template #cell(userId)="data">
          <div v-if="data.item.userId">
            <strong
              >{{ data.item.userId.name }}
              {{ data.item.userId.surname }}</strong
            >
            <br />
            <small class="text-muted">{{ data.item.userId.email }}</small>
          </div>
        </template>

        <template #cell(enrollmentStatus)="data">
          <b-badge
            v-if="data.item.enrollmentStatus"
            :variant="getEnrollmentStatusVariant(data.item.enrollmentStatus)"
          >
            {{ getEnrollmentStatusLabel(data.item.enrollmentStatus) }}
          </b-badge>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell(completedAt)="data">
          <span v-if="data.item.completedAt">
            {{ formatDate(data.item.completedAt) }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell(certificateNumber)="data">
          <div v-if="data.item.certificateNumber">
            <b-badge variant="success">
              <i class="fa fa-certificate mr-1"></i>
              {{ data.item.certificateNumber }}
            </b-badge>
            <br />
            <small class="text-muted">
              Emitido em {{ formatDate(data.item.certificateIssuedAt) }}
            </small>
          </div>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell(addedAt)="data">
          {{ formatDate(data.item.addedAt) }}
        </template>
      </b-table>
    </b-modal>

    <!-- Modal: Preview de Terceiros -->
    <b-modal
      v-model="showPreviewThirdPartiesModal"
      title="Terceiros que serão matriculados"
      size="lg"
      ok-only
    >
      <b-table
        :items="previewThirdParties"
        :fields="previewTableFields"
        striped
        hover
        responsive
      ></b-table>
    </b-modal>

    <!-- Modal: Preview de Todos os Participantes -->
    <b-modal
      v-model="showPreviewAllModal"
      title="Participantes que serão matriculados"
      size="lg"
      ok-only
    >
      <div class="mb-3">
        <b-badge variant="info" class="mr-2">
          {{ previewEmployeesCount }} Colaboradores
        </b-badge>
        <b-badge variant="warning">
          {{ previewThirdPartiesCount }} Terceiros
        </b-badge>
      </div>
      <b-table
        :items="previewAllParticipants"
        :fields="previewAllTableFields"
        striped
        hover
        responsive
      >
        <template #cell(type)="data">
          <b-badge
            :variant="data.item.type === 'employee' ? 'info' : 'warning'"
          >
            {{ data.item.type === "employee" ? "Colaborador" : "Terceiro" }}
          </b-badge>
        </template>
      </b-table>
    </b-modal>

    <!-- Modal Customizado: Aviso de Certificados -->
    <b-modal
      v-model="showValidationModal"
      hide-footer
      hide-header
      centered
      size="lg"
      modal-class="validation-modal"
    >
      <div class="validation-modal-content">
        <!-- Header -->
        <div class="validation-header">
          <div class="validation-icon">
            <i class="fa fa-exclamation-triangle"></i>
          </div>
          <h4 class="validation-title">
            Aviso - Alguns Colaboradores Não Serão Matriculados
          </h4>
        </div>

        <!-- Content -->
        <div class="validation-body">
          <!-- Colaboradores com certificado -->
          <div
            v-if="validationWarnings.hasCertificate.length > 0"
            class="warning-section certificate-section"
          >
            <div class="warning-section-header">
              <i class="fa fa-certificate"></i>
              <span>Colaboradores com Certificado Válido</span>
            </div>
            <div class="employee-list">
              <div
                v-for="emp in validationWarnings.hasCertificate"
                :key="emp._id"
                class="employee-item certificate-item"
              >
                <div class="employee-info">
                  <i class="fa fa-user-circle"></i>
                  <span class="employee-name">{{ emp.name }}</span>
                </div>
                <div class="employee-badge">
                  <b-badge variant="success">
                    <i class="fa fa-check-circle"></i>
                    Válido por {{ emp.daysRemaining }} dias
                  </b-badge>
                </div>
              </div>
            </div>
          </div>

          <!-- Colaboradores já matriculados -->
          <div
            v-if="validationWarnings.alreadyEnrolled.length > 0"
            class="warning-section enrolled-section"
          >
            <div class="warning-section-header">
              <i class="fa fa-user-graduate"></i>
              <span>Colaboradores Já Matriculados</span>
            </div>
            <div class="employee-list">
              <div
                v-for="emp in validationWarnings.alreadyEnrolled"
                :key="emp._id"
                class="employee-item enrolled-item"
              >
                <div class="employee-info">
                  <i class="fa fa-user-circle"></i>
                  <span class="employee-name">{{ emp.name }}</span>
                </div>
                <div class="employee-badge">
                  <b-badge variant="warning">
                    <i class="fa fa-graduation-cap"></i>
                    Já matriculado
                  </b-badge>
                </div>
              </div>
            </div>
          </div>

          <!-- Resumo -->
          <div class="validation-summary">
            <div class="summary-icon">
              <i class="fa fa-info-circle"></i>
            </div>
            <div class="summary-text">
              <strong>{{ validationWarnings.canEnroll }}</strong> de
              <strong>{{ validationWarnings.total }}</strong> colaboradores
              serão matriculados
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="validation-footer">
          <b-button
            variant="outline-secondary"
            size="lg"
            @click="cancelValidation"
          >
            <i class="fa fa-times mr-2"></i>
            Cancelar
          </b-button>
          <b-button variant="primary" size="lg" @click="confirmValidation">
            <i class="fa fa-check mr-2"></i>
            Continuar Mesmo Assim
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  getCourses,
  getEmployees,
  getDepartments,
  getBusinessUnits,
} from "@/api/course-assignment";
import { getClasses, createClass, updateClass, deleteClass } from "@/api/ead";

export default {
  name: "GerenciarTurmas",
  data() {
    return {
      classes: [],
      courses: [],
      employees: [],
      departments: [],
      businessUnits: [],
      loading: false,
      loadingCourses: false,
      loadingEmployees: false,
      loadingDepartments: false,
      loadingBusinessUnits: false,
      loadingThirdParties: false,
      loadingAllParticipants: false,
      submitting: false,
      showSidePanel: false,
      showEmployeeModal: false,
      showThirdPartyModal: false,
      showPreviewModal: false,
      showPreviewThirdPartiesModal: false,
      showPreviewAllModal: false,
      showStudentsModal: false,
      showValidationModal: false,
      validationWarnings: {
        hasCertificate: [],
        alreadyEnrolled: [],
        canEnroll: 0,
        total: 0,
      },
      validationResolve: null,
      editingClass: null,
      selectedClass: null,
      filterCourseId: null,
      filterStatus: null,
      currentPage: 1,
      perPage: 10,
      totalClasses: 0,
      employeeSearch: "",
      thirdPartySearch: "",
      selectedEmployees: [],
      tempSelectedEmployees: [],
      selectedThirdParties: [],
      tempSelectedThirdParties: [],
      previewEmployees: [],
      thirdParties: [],
      previewThirdParties: [],
      previewAllParticipants: [],
      form: {
        name: "",
        description: "",
        courseId: null,
        startDate: null,
        endDate: null,
        selectionType: "individual",
        departmentId: null,
        businessUnitId: null,
      },
      tableFields: [
        { key: "name", label: "Nome", sortable: true },
        { key: "courseId", label: "Curso", sortable: true },
        { key: "students", label: "Alunos", sortable: true },
        { key: "dates", label: "Datas" },
        { key: "status", label: "Status", sortable: true },
        { key: "actions", label: "Ações" },
      ],
      employeeTableFields: [
        { key: "selected", label: "" },
        { key: "name", label: "Nome", sortable: true },
        { key: "email", label: "Email", sortable: true },
        { key: "position", label: "Cargo" },
      ],
      thirdPartyTableFields: [
        { key: "selected", label: "" },
        { key: "name", label: "Nome", sortable: true },
        { key: "email", label: "Email", sortable: true },
        { key: "phone", label: "Telefone" },
      ],
      previewTableFields: [
        { key: "name", label: "Nome", sortable: true },
        { key: "email", label: "Email", sortable: true },
        { key: "position", label: "Cargo" },
      ],
      previewAllTableFields: [
        { key: "name", label: "Nome", sortable: true },
        { key: "email", label: "Email", sortable: true },
        { key: "type", label: "Tipo" },
      ],
      studentsTableFields: [
        { key: "userId", label: "Aluno" },
        { key: "enrollmentStatus", label: "Status" },
        { key: "completedAt", label: "Conclusão" },
        { key: "certificateNumber", label: "Certificado" },
        { key: "addedAt", label: "Data de Matrícula" },
      ],
      selectionTypeOptions: [
        { text: "Colaboradores", value: "individual" },
        { text: "Área/Setor", value: "department" },
        { text: "Terceiros", value: "thirdParty" },
        { text: "Todos", value: "all" },
      ],
      statusOptions: [
        { text: "Ativa", value: "active" },
        { text: "Concluída", value: "completed" },
        { text: "Cancelada", value: "cancelled" },
        { text: "Arquivada", value: "archived" },
      ],
    };
  },
  computed: {
    ...mapGetters(["profile"]),
    companyId() {
      return this.profile?.company;
    },
    courseOptions() {
      return this.courses.map((course) => ({
        value: course._id,
        text: course.nome,
      }));
    },
    courseFilterOptions() {
      return this.courses.map((course) => ({
        value: course._id,
        text: course.nome,
      }));
    },
    departmentOptions() {
      return this.departments.map((dept) => ({
        value: dept.value || dept._id,
        text: dept.value || dept.name,
      }));
    },
    businessUnitOptions() {
      return this.businessUnits.map((bu) => ({
        value: bu.value || bu._id,
        text: bu.value || bu.name,
      }));
    },
    filteredEmployees() {
      if (!this.employeeSearch) return this.employees;
      const search = this.employeeSearch.toLowerCase();
      return this.employees.filter(
        (emp) =>
          (emp.name && emp.name.toLowerCase().includes(search)) ||
          (emp.surname && emp.surname.toLowerCase().includes(search)) ||
          (emp.email && emp.email.toLowerCase().includes(search))
      );
    },
    filteredThirdParties() {
      if (!this.thirdPartySearch) return this.thirdParties;
      const search = this.thirdPartySearch.toLowerCase();
      return this.thirdParties.filter(
        (tp) =>
          (tp.name && tp.name.toLowerCase().includes(search)) ||
          (tp.email && tp.email.toLowerCase().includes(search)) ||
          (tp.phone && tp.phone.toLowerCase().includes(search))
      );
    },
    previewEmployeesCount() {
      return this.employees.length;
    },
    previewThirdPartiesCount() {
      return this.selectedThirdParties.length;
    },
  },
  mounted() {
    this.loadClasses();
    this.loadCourses();
    this.loadEmployees();
    this.loadDepartments();
    this.loadBusinessUnits();
  },
  methods: {
    async loadClasses() {
      this.loading = true;
      try {
        const params = {};
        if (this.filterCourseId) params.courseId = this.filterCourseId;
        if (this.filterStatus) params.status = this.filterStatus;

        const response = await getClasses(params);
        this.classes = response.data || response;
        this.totalClasses = this.classes.length;

        console.log("📚 Turmas carregadas:", this.classes.length);
      } catch (error) {
        console.error("Erro ao carregar turmas:", error);
        this.$bvToast.toast("Erro ao carregar turmas. Tente novamente.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      } finally {
        this.loading = false;
      }
    },
    async loadCourses() {
      this.loadingCourses = true;
      try {
        const response = await getCourses();
        this.courses = response.data || response;
        console.log("📚 Cursos carregados:", this.courses.length);
      } catch (error) {
        console.error("Erro ao carregar cursos:", error);
        this.$bvToast.toast("Erro ao carregar cursos. Tente novamente.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      } finally {
        this.loadingCourses = false;
      }
    },
    async loadEmployees() {
      this.loadingEmployees = true;
      try {
        const response = await getEmployees();
        this.employees = response.data.map((emp) => ({
          ...emp,
          name: emp.name || "",
          surname: emp.surname || "",
          email: emp.email || "",
          position: emp.position || "",
        }));
        console.log("👥 Colaboradores carregados:", this.employees.length);
      } catch (error) {
        console.error("Erro ao carregar colaboradores:", error);
        this.$bvToast.toast(
          "Erro ao carregar colaboradores. Tente novamente.",
          {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          }
        );
      } finally {
        this.loadingEmployees = false;
      }
    },
    async loadDepartments() {
      this.loadingDepartments = true;
      try {
        const response = await getDepartments();
        this.departments = response.data || response;
        console.log("🏢 Departamentos carregados:", this.departments.length);
      } catch (error) {
        console.error("Erro ao carregar departamentos:", error);
      } finally {
        this.loadingDepartments = false;
      }
    },
    async loadBusinessUnits() {
      this.loadingBusinessUnits = true;
      try {
        const response = await getBusinessUnits();
        this.businessUnits = response.data || response;
        console.log("💼 Cargos carregados:", this.businessUnits.length);
      } catch (error) {
        console.error("Erro ao carregar cargos:", error);
      } finally {
        this.loadingBusinessUnits = false;
      }
    },
    openAddClassPanel() {
      this.editingClass = null;
      this.resetForm();
      this.showSidePanel = true;
    },
    openEditClassPanel(classItem) {
      this.editingClass = classItem;
      this.form = {
        name: classItem.name,
        description: classItem.description || "",
        courseId: classItem.courseId._id || classItem.courseId,
        startDate: classItem.startDate
          ? this.formatDateForInput(classItem.startDate)
          : null,
        endDate: classItem.endDate
          ? this.formatDateForInput(classItem.endDate)
          : null,
        selectionType: "individual",
        departmentId: null,
        businessUnitId: null,
        notifyByEmail: true,
      };
      this.showSidePanel = true;
    },
    openEmployeeModal() {
      this.tempSelectedEmployees = [...this.selectedEmployees];
      this.showEmployeeModal = true;
      this.$nextTick(() => {
        if (this.$refs.employeeTable) {
          this.selectedEmployees.forEach((emp) => {
            const index = this.employees.findIndex((e) => e._id === emp._id);
            if (index >= 0) {
              this.$refs.employeeTable.selectRow(index);
            }
          });
        }
      });
    },
    async openThirdPartyModal() {
      // Carregar terceiros se ainda não carregou
      if (this.thirdParties.length === 0) {
        await this.loadThirdParties();
      }
      this.tempSelectedThirdParties = [...this.selectedThirdParties];
      this.showThirdPartyModal = true;
      this.$nextTick(() => {
        if (this.$refs.thirdPartyTable) {
          this.selectedThirdParties.forEach((tp) => {
            const index = this.thirdParties.findIndex((t) => t._id === tp._id);
            if (index >= 0) {
              this.$refs.thirdPartyTable.selectRow(index);
            }
          });
        }
      });
    },
    async openStudentsModal(classItem) {
      try {
        // Buscar detalhes completos da turma com status e certificados
        const { getClass } = await import("@/api/ead");
        const response = await getClass(classItem._id);
        this.selectedClass = response.data || response;
        this.showStudentsModal = true;
      } catch (error) {
        console.error("Erro ao carregar detalhes da turma:", error);
        this.$bvToast.toast(
          "Erro ao carregar alunos da turma. Tente novamente.",
          {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          }
        );
      }
    },
    onRowSelected(items) {
      this.tempSelectedEmployees = items;
    },
    onThirdPartyRowSelected(items) {
      this.tempSelectedThirdParties = items;
    },
    confirmEmployeeSelection() {
      this.selectedEmployees = [...this.tempSelectedEmployees];
    },
    confirmThirdPartySelection() {
      this.selectedThirdParties = [...this.tempSelectedThirdParties];
    },
    removeEmployee(empId) {
      this.selectedEmployees = this.selectedEmployees.filter(
        (e) => e._id !== empId
      );
    },
    removeThirdParty(tpId) {
      this.selectedThirdParties = this.selectedThirdParties.filter(
        (tp) => tp._id !== tpId
      );
    },
    resetEmployeeSelection() {
      this.selectedEmployees = [];
      this.selectedThirdParties = [];
      this.previewEmployees = [];
      this.previewThirdParties = [];
      this.previewAllParticipants = [];
      this.form.departmentId = null;
      this.form.businessUnitId = null;

      // Carregar dados conforme o tipo de seleção
      if (this.form.selectionType === "thirdParty") {
        this.loadThirdParties();
      } else if (this.form.selectionType === "all") {
        this.loadAllParticipants();
      }
    },
    async loadThirdParties() {
      this.loadingThirdParties = true;
      try {
        // Usar a API de relacionamentos para buscar terceiros
        const { getThirdParties } = await import("@/api/relationships");
        const response = await getThirdParties(this.companyId);
        const data = response.data || response;
        this.thirdParties = Array.isArray(data) ? data : [];
        this.previewThirdParties = this.thirdParties.map((tp) => ({
          ...tp,
          name: tp.name || tp.nome || tp.fullName || "",
          email: tp.email || "",
          type: "thirdParty",
        }));
        console.log(
          "👥 Terceiros carregados:",
          this.previewThirdParties.length
        );
      } catch (error) {
        console.error("Erro ao carregar terceiros:", error);
        this.previewThirdParties = [];
        this.$bvToast.toast("Erro ao carregar terceiros. Tente novamente.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      } finally {
        this.loadingThirdParties = false;
      }
    },
    async loadAllParticipants() {
      this.loadingAllParticipants = true;
      try {
        // Carregar colaboradores e terceiros
        await Promise.all([this.loadEmployees(), this.loadThirdParties()]);

        // Combinar colaboradores e terceiros
        const employeesWithType = this.employees.map((emp) => ({
          ...emp,
          type: "employee",
        }));

        this.previewAllParticipants = [
          ...employeesWithType,
          ...this.previewThirdParties,
        ];

        console.log(
          "👥 Total de participantes:",
          this.previewAllParticipants.length
        );
      } catch (error) {
        console.error("Erro ao carregar participantes:", error);
      } finally {
        this.loadingAllParticipants = false;
      }
    },
    async previewDepartmentEmployees() {
      if (!this.form.departmentId) {
        this.previewEmployees = [];
        return;
      }

      this.previewEmployees = this.employees.filter(
        (emp) => emp.area === this.form.departmentId
      );
    },
    async previewBusinessUnitEmployees() {
      if (!this.form.businessUnitId) {
        this.previewEmployees = [];
        return;
      }

      this.previewEmployees = this.employees.filter(
        (emp) => emp.position === this.form.businessUnitId
      );
    },
    async submitClass() {
      this.submitting = true;
      try {
        // Preparar dados da turma
        const classData = {
          name: this.form.name,
          description: this.form.description,
          courseId: this.form.courseId,
          startDate: this.form.startDate || undefined,
          endDate: this.form.endDate || undefined,
        };

        // Preparar lista de participantes
        let employeeIds = [];
        let thirdPartyIds = [];

        if (this.form.selectionType === "individual") {
          employeeIds = this.selectedEmployees.map((emp) => emp._id);
        } else if (this.form.selectionType === "department") {
          employeeIds = this.previewEmployees.map((emp) => emp._id);
        } else if (this.form.selectionType === "thirdParty") {
          thirdPartyIds = this.selectedThirdParties.map((tp) => tp._id);
        } else if (this.form.selectionType === "all") {
          employeeIds = this.employees.map((emp) => emp._id);
          thirdPartyIds = this.thirdParties.map((tp) => tp._id);
        }

        // Validar se há participantes selecionados
        if (employeeIds.length === 0 && thirdPartyIds.length === 0) {
          this.$bvToast.toast(
            "Selecione pelo menos um colaborador ou terceiro",
            {
              title: "Atenção",
              toastClass: "custom-toast custom-toast-warning",
              variant: "light",
              solid: true,
              autoHideDelay: 8000,
              noCloseButton: false,
            }
          );
          this.submitting = false;
          return;
        }

        // Se for criação nova, validar elegibilidade antes
        if (!this.editingClass && employeeIds.length > 0) {
          const { validateEnrollments } = await import(
            "@/api/course-assignment"
          );

          console.log("🔍 Validando elegibilidade de colaboradores...");

          const validationResponse = await validateEnrollments({
            courseId: this.form.courseId,
            participants: {
              type: "individual",
              employeeIds: employeeIds,
            },
          });

          const validation = validationResponse.data || validationResponse;

          console.log("📊 Resultado da validação:", validation);

          // Se não houver ninguém elegível para matrícula (e não houver terceiros)
          if (validation.canEnroll.length === 0 && thirdPartyIds.length === 0) {
            this.$bvModal.msgBoxOk(
              "Todos os colaboradores selecionados já possuem matrícula ativa ou certificado válido neste curso.",
              {
                title: "⚠️ Nenhum Colaborador Elegível",
                size: "md",
                buttonSize: "sm",
                okVariant: "primary",
                headerClass: "p-3 border-bottom-0",
                footerClass: "p-3 border-top-0",
                centered: true,
              }
            );
            this.submitting = false;
            return;
          }

          // Mostrar aviso se houver colaboradores filtrados
          const hasWarnings =
            (validation.hasCertificate &&
              validation.hasCertificate.length > 0) ||
            (validation.alreadyEnrolled &&
              validation.alreadyEnrolled.length > 0);

          if (hasWarnings) {
            // Preparar dados para o modal customizado
            this.validationWarnings = {
              hasCertificate: validation.hasCertificate || [],
              alreadyEnrolled: validation.alreadyEnrolled || [],
              canEnroll: validation.canEnroll.length,
              total: validation.total,
            };

            // Mostrar modal customizado e aguardar resposta
            const confirmed = await this.showValidationWarning();

            if (!confirmed) {
              this.submitting = false;
              return;
            }
          }
        }

        // Preparar payload para designação
        const assignmentPayload = {
          courseId: this.form.courseId,
          participants: {
            type:
              this.form.selectionType === "thirdParty"
                ? "thirdParty"
                : this.form.selectionType === "all"
                ? "all"
                : "individual",
            employeeIds: employeeIds,
            thirdPartyIds: thirdPartyIds,
          },
          notifyByEmail: true,
          createClass: true,
          classData: classData,
        };

        if (this.editingClass) {
          // Atualizar turma existente
          await updateClass(this.editingClass._id, classData);
          this.$bvToast.toast("Turma atualizada com sucesso!", {
            title: "Sucesso",
            toastClass: "custom-toast custom-toast-success",
            variant: "light",
            solid: true,
            autoHideDelay: 5000,
            noCloseButton: false,
          });
        } else {
          // Criar nova turma com designação
          // Usar a API de designação que já cria turma e matricula alunos
          const { assignCourse } = await import("@/api/course-assignment");
          await assignCourse(assignmentPayload);
          this.$bvToast.toast(
            "Turma criada e participantes matriculados com sucesso!",
            {
              title: "Sucesso",
              toastClass: "custom-toast custom-toast-success",
              variant: "light",
              solid: true,
              autoHideDelay: 5000,
              noCloseButton: false,
            }
          );
        }

        this.showSidePanel = false;
        this.loadClasses();
      } catch (error) {
        console.error("Erro ao salvar turma:", error);
        this.$bvToast.toast(
          error.response?.data?.error ||
            "Erro ao salvar turma. Tente novamente.",
          {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          }
        );
      } finally {
        this.submitting = false;
      }
    },
    confirmDelete(classItem) {
      this.$bvModal
        .msgBoxConfirm(
          `Tem certeza que deseja excluir a turma "${classItem.name}"?`,
          {
            title: "Confirmar Exclusão",
            size: "md",
            buttonSize: "sm",
            okVariant: "danger",
            okTitle: "Sim, Excluir",
            cancelTitle: "Cancelar",
            footerClass: "p-2",
            hideHeaderClose: false,
            centered: true,
          }
        )
        .then((value) => {
          if (value) {
            this.deleteClassItem(classItem._id);
          }
        });
    },
    async deleteClassItem(classId) {
      try {
        await deleteClass(classId);
        this.$bvToast.toast("Turma excluída com sucesso!", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
        this.loadClasses();
      } catch (error) {
        console.error("Erro ao excluir turma:", error);
        this.$bvToast.toast("Erro ao excluir turma. Tente novamente.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      }
    },
    resetForm() {
      this.form = {
        name: "",
        description: "",
        courseId: null,
        startDate: null,
        endDate: null,
        selectionType: "individual",
        departmentId: null,
        businessUnitId: null,
      };
      this.selectedEmployees = [];
      this.selectedThirdParties = [];
      this.previewEmployees = [];
      this.previewThirdParties = [];
      this.previewAllParticipants = [];
      this.editingClass = null;
    },
    onPageChange(page) {
      this.currentPage = page;
    },
    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("pt-BR");
    },
    formatDateForInput(date) {
      if (!date) return null;
      const d = new Date(date);
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const year = d.getFullYear();
      return `${year}-${month}-${day}`;
    },
    getStatusVariant(status) {
      const variants = {
        active: "success",
        completed: "info",
        cancelled: "danger",
        archived: "secondary",
      };
      return variants[status] || "secondary";
    },
    getStatusLabel(status) {
      const labels = {
        active: "Ativa",
        completed: "Concluída",
        cancelled: "Cancelada",
        archived: "Arquivada",
      };
      return labels[status] || status;
    },
    getEnrollmentStatusVariant(status) {
      const variants = {
        active: "primary",
        completed: "success",
        cancelled: "danger",
      };
      return variants[status] || "secondary";
    },
    getEnrollmentStatusLabel(status) {
      const labels = {
        active: "Cursando",
        completed: "Concluído",
        cancelled: "Cancelado",
      };
      return labels[status] || status;
    },
    showValidationWarning() {
      return new Promise((resolve) => {
        this.validationResolve = resolve;
        this.showValidationModal = true;
      });
    },
    confirmValidation() {
      this.showValidationModal = false;
      if (this.validationResolve) {
        this.validationResolve(true);
        this.validationResolve = null;
      }
    },
    cancelValidation() {
      this.showValidationModal = false;
      if (this.validationResolve) {
        this.validationResolve(false);
        this.validationResolve = null;
      }
    },
  },
};
</script>

<style scoped>
.filters-section {
  display: flex;
  gap: 15px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.filter-item {
  flex: 1;
  min-width: 200px;
}

.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  overflow: hidden;
}

::v-deep .classes-header th {
  background-color: #f8f9fa;
  color: #495057;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  padding: 1rem 0.75rem;
  border: none;
}

::v-deep .table td {
  vertical-align: middle;
  padding: 0.75rem;
  border-top: 1px solid #e9ecef;
}

::v-deep .table tbody tr {
  transition: background-color 0.2s ease;
}

::v-deep .table tbody tr:hover {
  background-color: #f8f9fa;
}

.class-name {
  color: #2c3e50;
  font-weight: 500;
}

.action-btn {
  padding: 0.25rem 0.5rem;
  color: #6c757d;
  transition: all 0.2s ease;
}

.action-btn:hover {
  color: #007bff;
  background-color: transparent;
}

.action-icon {
  font-size: 1rem;
}

.pagination-nav {
  margin-top: 1.5rem;
  display: flex;
  justify-content: center;
}

::v-deep .pagination {
  margin-bottom: 0;
}

::v-deep .page-link {
  color: #007bff;
  border-color: #dee2e6;
}

::v-deep .page-item.active .page-link {
  background-color: #007bff;
  border-color: #007bff;
}

/* Sidebar customizado */
::v-deep .sidebar-custom {
  background: #f8f9fa;
}

::v-deep .sidebar-custom .b-sidebar-header {
  background: white;
  color: #2c3e50;
  padding: 1.5rem;
  font-weight: 600;
  font-size: 1.25rem;
  border-bottom: 2px solid #e9ecef;
}

::v-deep .sidebar-custom .b-sidebar-body {
  padding: 0;
}

.sidebar-content {
  padding: 1.5rem;
  height: 100%;
  overflow-y: auto;
}

/* Seções do formulário */
.form-section {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.section-header {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 1rem;
  color: #495057;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e9ecef;
}

.section-header i {
  margin-right: 0.75rem;
  color: #667eea;
  font-size: 1.1rem;
}

/* Campos obrigatórios */
.required-field label::after {
  content: " *";
  color: #dc3545;
  font-weight: 600;
}

/* Botões de seleção */
.selection-type-buttons {
  width: 100%;
}

.selection-type-buttons ::v-deep .btn {
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
}

.selection-content {
  margin-top: 1rem;
}

/* Lista de colaboradores selecionados */
.selected-employees {
  max-height: 300px;
  overflow-y: auto;
}

.employee-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.employee-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  transition: all 0.2s ease;
}

.employee-item:hover {
  background: #e9ecef;
  border-color: #dee2e6;
}

.employee-info {
  flex: 1;
}

.employee-name {
  font-weight: 500;
  color: #2c3e50;
  font-size: 0.9rem;
}

.employee-email {
  font-size: 0.8rem;
  color: #6c757d;
  margin-top: 0.2rem;
}

.remove-btn {
  color: #dc3545;
  padding: 0.25rem 0.5rem;
}

.remove-btn:hover {
  color: #c82333;
  background-color: transparent;
}

/* Alert de preview */
.preview-alert {
  margin-bottom: 0;
  border-radius: 6px;
  font-size: 0.9rem;
}

/* Checkbox customizado */
.custom-checkbox {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  margin-bottom: 0;
}

.custom-checkbox:hover {
  background: #e9ecef;
}

/* Botões de ação */
.form-actions {
  position: sticky;
  bottom: 0;
  background: white;
  padding: 1.5rem;
  margin: 0 -1.5rem -1.5rem;
  border-top: 2px solid #e9ecef;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.form-actions .btn {
  font-weight: 500;
}

/* Inputs maiores */
::v-deep .form-control-lg {
  font-size: 1rem;
}

/* Scrollbar customizada */
.sidebar-content::-webkit-scrollbar {
  width: 6px;
}

.sidebar-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.sidebar-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.sidebar-content::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.b-sidebar {
  z-index: 1050;
}

/* Garantir que notificações fiquem acima do sidebar e centralizadas */
::v-deep .vue-notification-group {
  z-index: 10000 !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  width: auto !important;
  max-width: 500px !important;
}

::v-deep .vue-notification {
  z-index: 10000 !important;
  margin: 0.5rem auto !important;
  padding: 1rem 1.5rem !important;
  font-size: 1rem !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
}

::v-deep .vue-notification .notification-title {
  font-weight: 600 !important;
  font-size: 1.1rem !important;
  margin-bottom: 0.25rem !important;
}

::v-deep .vue-notification .notification-content {
  opacity: 0.95 !important;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter,
.slide-leave-to {
  transform: translateX(-10px);
  opacity: 0;
}

/* Modal de Validação Customizado */
::v-deep .validation-modal .modal-dialog {
  max-width: 700px;
}

::v-deep .validation-modal .modal-content {
  border: none;
  border-radius: 12px;
  overflow: hidden;
}

.validation-modal-content {
  padding: 0;
}

.validation-header {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  padding: 2rem;
  text-align: center;
  color: white;
}

.validation-icon {
  font-size: 3.5rem;
  margin-bottom: 1rem;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.validation-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.4;
}

.validation-body {
  padding: 2rem;
  background: #f8f9fa;
}

.warning-section {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.warning-section:last-of-type {
  margin-bottom: 1rem;
}

.warning-section-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 600;
  font-size: 1.1rem;
  color: #495057;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e9ecef;
}

.warning-section-header i {
  font-size: 1.3rem;
}

.certificate-section .warning-section-header {
  color: #10b981;
}

.certificate-section .warning-section-header i {
  color: #10b981;
}

.enrolled-section .warning-section-header {
  color: #f59e0b;
}

.enrolled-section .warning-section-header i {
  color: #f59e0b;
}

.employee-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.employee-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 4px solid;
  transition: all 0.2s;
}

.employee-item:hover {
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.certificate-item {
  border-left-color: #10b981;
}

.enrolled-item {
  border-left-color: #f59e0b;
}

.employee-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.employee-info i {
  font-size: 1.5rem;
  color: #6c757d;
}

.employee-name {
  font-weight: 500;
  font-size: 1rem;
  color: #212529;
}

.employee-badge {
  flex-shrink: 0;
}

.employee-badge .badge {
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
  font-weight: 500;
}

.validation-summary {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: white;
  border-radius: 8px;
  border: 2px solid #3490dc;
  box-shadow: 0 2px 4px rgba(52, 144, 220, 0.1);
}

.summary-icon {
  font-size: 2rem;
  color: #3490dc;
  flex-shrink: 0;
}

.summary-text {
  font-size: 1.1rem;
  color: #495057;
  line-height: 1.5;
}

.summary-text strong {
  color: #3490dc;
  font-weight: 700;
}

.validation-footer {
  display: flex;
  gap: 1rem;
  padding: 1.5rem 2rem;
  background: white;
  border-top: 1px solid #e9ecef;
}

.validation-footer .btn {
  flex: 1;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  transition: all 0.2s;
}

.validation-footer .btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.validation-footer .btn i {
  font-size: 1.1rem;
}
</style>
